import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@/api/httpClient'
import { projectApi } from '@/api/projectApi'
import { useDraftStore } from '@/stores/draftStore'
import { useDiagramStore } from '@/stores/diagramStore'
import { useViewportStore } from '@/stores/viewportStore'
import { useTabViewStore } from '@/stores/tabViewStore'
import { useHistoryStore } from '@/stores/historyStore'
import { useTabsStore } from '@/stores/tabsStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useProjectMembersStore } from '@/stores/projectMembersStore'
import { metadataToEditorState } from '@/services/metadata/adapters'
import { keyedToArray } from '@/services/metadata/keyed'
import { getDraftBackup, clearDraftBackup, type CachedDraftEntry } from '@/services/offlineCache'
import type { Project } from '@/api/types'
import { emptyMetadataV1, type ProjectMetadataV1 } from '@/services/metadata/schema'

export type LoadProjectStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface UseLoadProjectResult {
  status: LoadProjectStatus
  project: Project | null
  error: string | null
  errorStatus: number | null
  errorCode: string | null
  /** Backup local mais novo que o draft remoto, aguardando decisão do usuário. */
  pendingBackup: CachedDraftEntry | null
  /** Hidrata o editor a partir do backup local e descarta-o. */
  recoverFromBackup: () => void
  /** Descarta o backup local sem hidratar. */
  dismissBackup: () => Promise<void>
}

function isValidMetadata(meta: unknown): meta is ProjectMetadataV1 {
  if (!meta || typeof meta !== 'object') return false
  const m = meta as Partial<ProjectMetadataV1>
  return Array.isArray(m.tables) && Array.isArray(m.relationships)
}

/**
 * `fromServer` marca a carga como adoção do estado remoto, não como edição:
 * ela roda com o autosave suprimido para não entrar no conjunto de entidades
 * tocadas localmente — do contrário o primeiro save após abrir o projeto sairia
 * sem escopo e sobrescreveria quem estivesse editando junto. A restauração de
 * backup local NÃO usa a flag: ali o conteúdo é local e precisa ser enviado.
 */
function hydrateStores(metadata: ProjectMetadataV1, opts?: { fromServer?: boolean }) {
  const state = metadataToEditorState(metadata)
  const fromServer = opts?.fromServer ?? false

  if (fromServer) useDraftStore.setState({ autosaveSuppressed: true })
  useDiagramStore.getState().loadDiagram({
    tables: state.diagram.tables,
    rels: state.diagram.rels,
    groups: state.diagram.groups,
    defaults: state.diagram.defaults,
    projectName: state.diagram.projectName,
    dialect: state.diagram.dialect,
  })
  if (fromServer) useDraftStore.setState({ autosaveSuppressed: false })

  useViewportStore.getState().setViewport(
    state.viewport.sc,
    state.viewport.tx,
    state.viewport.ty,
  )

  useTabViewStore.getState().setHiddenTableIds(state.tabView.hiddenTableIds)

  useHistoryStore.getState().initHistory({
    ...state.diagram,
    hiddenTableIds: state.tabView.hiddenTableIds,
  })
}

function isActiveProjectTab(projectId: number) {
  const tabsState = useTabsStore.getState()
  const activeTab = tabsState.tabs.find((tab) => tab.id === tabsState.activeTabId)
  return Number(activeTab?.projectId) === projectId
}

export function useLoadProject(projectId: number): UseLoadProjectResult {
  const [status, setStatus] = useState<LoadProjectStatus>(() => (
    isActiveProjectTab(projectId) ? 'ready' : 'idle'
  ))
  const [project, setProject] = useState<Project | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [pendingBackup, setPendingBackup] = useState<CachedDraftEntry | null>(null)
  const loadDraft = useDraftStore((s) => s.loadDraft)
  const setCurrentProjectId = useDraftStore((s) => s.setCurrentProjectId)
  const setCurrentProjectName = useDraftStore((s) => s.setCurrentProjectName)

  const load = useCallback(async () => {
    const preserveVisibleEditor = isActiveProjectTab(projectId)
    if (!preserveVisibleEditor) {
      setStatus('loading')
    }
    setError(null)
    setErrorStatus(null)
    setErrorCode(null)
    setPendingBackup(null)

    try {
      const [proj, draftResult] = await Promise.all([
        projectApi.get(projectId, 'current_commit'),
        loadDraft(projectId),
      ])

      // Garante que workspaceStore.currentWorkspace reflete o workspace do projeto.
      // Sem isso, ações que dependem de currentWorkspace (ex.: Importar SQL no editor)
      // disparam "nenhum workspace ativo".
      try {
        const ws = useWorkspaceStore.getState()
        if (ws.currentWorkspace?.id !== proj.workspace) {
          if (!ws.workspaces.length) {
            // Carrega lista usando memberships do authStore.
            const { useAuthStore } = await import('@/stores/authStore')
            const memberships = useAuthStore.getState().memberships
            await ws.loadWorkspaces(memberships).catch(() => {/* best-effort */})
          }
          await useWorkspaceStore.getState().selectWorkspaceById(proj.workspace).catch(() => {/* best-effort */})
        }
        await useWorkspaceStore.getState().loadSubscription(proj.workspace).catch(() => {/* best-effort */})
      } catch { /* best-effort: workspace pode ainda não estar pronto */ }

      setProject(proj)

      // Hydrate myProjectRole so useProjectCan works immediately in the editor.
      const projectRole = proj.my_project_role ?? null
      useProjectMembersStore.getState().setMyProjectRole(projectRole)

      // Force view mode for VIEWERs (req 7 / F4.5). WS owner/admin are elevated
      // so they keep edit access even without a project-level role.
      const wsRole = useWorkspaceStore.getState().myRole
      const isWsElevated = wsRole !== null && wsRole <= 2
      if (projectRole === 4 && !isWsElevated) {
        useDraftStore.getState().setMode('view')
      }

      // Keep projectsStore in sync so other features (e.g. conflict resolution)
      // can read this project's HEAD commit even when the editor was opened by
      // deep link without ever visiting the project list. Upsert (not insert)
      // so a stale current_commit_id gets refreshed.
      try {
        const { useProjectsStore } = await import('@/stores/projectsStore')
        const exists = useProjectsStore.getState().projects.some((p) => p.id === proj.id)
        if (exists) {
          useProjectsStore.setState((s) => ({
            projects: s.projects.map((p) =>
              p.id === proj.id ? { ...p, current_commit_id: proj.current_commit_id } : p,
            ),
          }))
        } else {
          useProjectsStore.getState().insertProject(proj)
        }
      } catch { /* best-effort */ }

      const draft = draftResult

      const tryHydrate = (meta: unknown, source: 'draft' | 'commit'): boolean => {
        if (!isValidMetadata(meta)) {
          if (meta != null) {
            // eslint-disable-next-line no-console
            console.warn(`[useLoadProject] metadata inválida vinda de ${source}; ignorando.`)
          }
          return false
        }
        try {
          hydrateStores(meta, { fromServer: true })
          return true
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`[useLoadProject] falha ao hidratar a partir de ${source}:`, err)
          return false
        }
      }

      // Resolve a metadata do commit HEAD. A expansão `current_commit` do
      // endpoint do projeto NÃO inclui `metadata` (igual à lista de commits),
      // então quando não há draft precisamos buscar o commit completo —
      // senão a hidratação falha em silêncio e o editor continua mostrando o
      // projeto ANTERIOR que ainda está no diagramStore.
      const resolveCommitMeta = async (): Promise<unknown> => {
        const inline = proj.current_commit?.metadata
        if (isValidMetadata(inline)) return inline
        const headCommitId = proj.current_commit_id
        if (headCommitId == null) return null
        try {
          const { commitApi } = await import('@/api/commitApi')
          const full = await commitApi.get(projectId, headCommitId)
          return full.metadata ?? null
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[useLoadProject] falha ao buscar metadata do commit HEAD:', err)
          return null
        }
      }

      const hydrated = tryHydrate(keyedToArray(draft.metadata as never), 'draft')
      if (!hydrated) {
        const commitMeta = await resolveCommitMeta()
        if (!tryHydrate(commitMeta, 'commit')) {
          hydrateStores(emptyMetadataV1(), { fromServer: true })
        }
      }

      setCurrentProjectId(projectId)
      setCurrentProjectName(proj.name)

      // Check for local backup newer than the remote draft (or any backup, if no draft loaded)
      try {
        const backup = await getDraftBackup(projectId)
        if (backup) {
          const remoteAt = new Date(draft.modified_at ?? draft.created_at ?? 0).getTime()
          const backupAt = new Date(backup.cachedAt).getTime()
          if (backupAt > remoteAt) {
            setPendingBackup(backup)
          } else {
            void clearDraftBackup(projectId)
          }
        }
      } catch { /* best-effort */ }

      // Sync with tabsStore so the TabBar shows the open project (F7.1)
      const remoteId = String(projectId)
      const tabsState = useTabsStore.getState()
      const existing = tabsState.tabs.find((t) => t.projectId === remoteId)
      if (existing) {
        useTabsStore.getState().markClean(existing.id)
        useTabsStore.setState((state) => ({
          activeTabId: existing.id,
          tabs: state.tabs.map((tab) => (
            tab.id === existing.id
              ? {
                  ...tab,
                  projectId: remoteId,
                  workspaceId: proj.workspace,
                  remoteName: proj.name,
                  title: proj.name,
                  dialect: (proj.dialect as typeof tab.dialect | undefined) ?? tab.dialect,
                }
              : tab
          )),
        }))
      } else {
        const tabId = tabsState.createTabFromActiveStores()
        useTabsStore.setState((s) => ({
          activeTabId: tabId,
          tabs: s.tabs.map((t) => (
            t.id === tabId
              ? {
                  ...t,
                  projectId: remoteId,
                  workspaceId: proj.workspace,
                  remoteName: proj.name,
                  title: proj.name,
                  dialect: (proj.dialect as typeof t.dialect | undefined) ?? t.dialect,
                }
              : t
          )),
        }))
      }

      setStatus('ready')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar projeto'
      setError(msg)
      setErrorStatus(err instanceof ApiError ? err.status : null)
      setErrorCode(err instanceof ApiError ? err.code : null)
      setStatus('error')
    }
  }, [projectId, loadDraft])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => () => {
    setCurrentProjectId(null)
    setCurrentProjectName(null)
  }, [setCurrentProjectId, setCurrentProjectName])

  const recoverFromBackup = useCallback(() => {
    if (!pendingBackup) return
    hydrateStores(pendingBackup.metadata)
    void clearDraftBackup(pendingBackup.projectId)
    // Marca dirty para que o próximo save persista o estado recuperado
    void import('@/stores/draftStore').then(({ useDraftStore }) => {
      useDraftStore.getState().markDirty()
    })
    setPendingBackup(null)
  }, [pendingBackup])

  const dismissBackup = useCallback(async () => {
    if (!pendingBackup) return
    await clearDraftBackup(pendingBackup.projectId)
    setPendingBackup(null)
  }, [pendingBackup])

  return {
    status,
    project,
    error,
    errorStatus,
    errorCode,
    pendingBackup,
    recoverFromBackup,
    dismissBackup,
  }
}
